import { HttpClient } from "@angular/common/http";
import { EventEmitter, inject, Injectable } from "@angular/core";
import { AccountInfo as AzureAccountInfo } from "@azure/msal-common/browser";
import { firstValueFrom } from "rxjs";
import { AccountInfo, UserConfig } from "src/app/common/models/account.model";
import { AbstractService } from "src/app/common/services/abstract.service";
import { TimedCache } from "src/app/common/tools/TimedCache";

const API_PATH = "/api/internal/accounts";

@Injectable({
  providedIn: "root",
})
export class AccountService extends AbstractService {
  private accountName = "";
  private isAdmin = false;

  private readonly httpClient = inject(HttpClient);

  private readonly accountsCache = new TimedCache(
    3600 * 1000,
    async () => await firstValueFrom(this.httpClient.get<AccountInfo[]>(`${API_PATH}/find-all`)),
  );

  private readonly localAccountInfo = new TimedCache(
    3600 * 1000,
    async () => await firstValueFrom(this.httpClient.get<AccountInfo>(API_PATH)),
  );

  userConfigChange = new EventEmitter<UserConfig>();

  async getAllAccounts(): Promise<AccountInfo[]> {
    try {
      return await this.accountsCache.getValue();
    } catch (err: unknown) {
      console.error(err);
      return [];
    }
  }

  async getLocalAccount(): Promise<AccountInfo> {
    return await this.localAccountInfo.getValue();
  }

  async updateLastLogin(accountId: string, role?: string): Promise<void> {
    await firstValueFrom(this.httpClient.post(`${API_PATH}/${accountId}/update-last-login/${role}`, null));
  }

  async updateUserConfig(userConfig: UserConfig): Promise<void> {
    const accountInfo = await firstValueFrom(
      this.httpClient.post<AccountInfo>(`${API_PATH}/update-user-config`, userConfig),
    );

    this.userConfigChange.emit(userConfig);
    this.localAccountInfo.setValue(accountInfo);
  }

  async getAllRoles(): Promise<string[]> {
    return await firstValueFrom(this.httpClient.get<string[]>(`${API_PATH}/roles`));
  }

  async getBusinessUnit(): Promise<string> {
    return await firstValueFrom(this.httpClient.get<string>(`${API_PATH}/businessunit`));
  }

  getAccountName(): string {
    return this.accountName;
  }
  setAccountName(accountName: string): void {
    this.accountName = accountName;
  }

  getIsAdmin(): boolean {
    return this.isAdmin;
  }
  setIsAdmin(isAdmin: boolean): void {
    this.isAdmin = isAdmin;
  }
  setAccountInfo(account: AzureAccountInfo): void {
    this.accountName = account.name!;
    this.isAdmin = account.idTokenClaims?.roles?.includes("admin") ?? false;
  }
}
